import { ArrowRight, CalendarCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatNumber, formatPercent } from '@/utils/format'
import type { DashboardData } from '../api'

/** Today's register progress: how many sections have been marked and the present rate. */
export function AttendanceTodayCard({ data }: { data: NonNullable<DashboardData['attendanceToday']> }) {
  const { t } = useTranslation('dashboard')
  const progress = data.sectionsTotal ? Math.round((data.sectionsMarked / data.sectionsTotal) * 100) : 0
  const rows = [
    { label: 'Present', value: data.present, dot: 'bg-success-500' },
    { label: 'Late', value: data.late, dot: 'bg-warning-500' },
    { label: 'Absent', value: data.absent, dot: 'bg-danger-500' },
    { label: 'Excused', value: data.excused, dot: 'bg-info-500' },
  ]
  return (
    <Card>
      <CardHeader
        title={t('Attendance today')}
        icon={<CalendarCheck />}
        actions={
          <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            <Link to="/admin/attendance">{t('Register')}</Link>
          </Button>
        }
      />
      <CardBody>
        {!data.isWorkingDay ? (
          <p className="text-sm text-muted">{t('No school today — no register to take.')}</p>
        ) : (
          <>
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-[1.75rem] leading-none font-semibold text-ink tabular">
                  {data.rate !== null ? formatPercent(data.rate) : '—'}
                </p>
                <p className="mt-1 text-xs text-muted">{t('present rate of students marked')}</p>
              </div>
              <p className="text-right text-xs text-muted">
                <span className="font-semibold text-ink tabular">
                  {data.sectionsMarked}/{data.sectionsTotal}
                </span>{' '}
                {t('sections marked')}
              </p>
            </div>
            <div
              className="mt-3 h-2 rounded-full bg-surface-sunken"
              role="progressbar"
              aria-label={t('Sections with attendance marked today')}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${progress}%` }} />
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2.5">
              {rows.map((r) => (
                <li key={r.label} className="flex items-center gap-2 text-sm">
                  <span className={`size-2 rounded-full ${r.dot}`} aria-hidden="true" />
                  <span className="flex-1 text-ink-soft">{t(r.label)}</span>
                  <span className="font-semibold text-ink tabular">{formatNumber(r.value)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  )
}
